import React, { useState, useEffect } from 'react';
import { clientsApi, auditsApi } from '../services/api';
import { User } from '../contexts/AuthContext';

interface CreateProgrammedAuditFormProps {
  user: User;
  onSuccess: () => void;
  onCancel: () => void;
}

interface Client {
  id: number;
  nombre: string;
  email: string;
  telefono?: string;
  empresa?: string;
  direccion?: string;
  costoAuditoria?: number;
  tiempoEstimado?: string;
  comisionOperador?: number;
  montoFijoOperador?: number;
  tipoComision?: string;
  branches?: Array<{
    id: number;
    nombre: string;
    direccion: string;
    telefono?: string;
    precio_normal?: number;
    precio_prioritaria?: number;
    precio_urgente?: number;
  }>;
  negociosHijos?: Array<{
    id: number;
    nombre: string;
    branches?: Array<{
      id: number;
      nombre: string;
      direccion: string;
      telefono?: string;
    }>;
  }>;
}

const CreateProgrammedAuditForm: React.FC<CreateProgrammedAuditFormProps> = ({ user, onSuccess, onCancel }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [formData, setFormData] = useState({
    clienteId: '',
    branchId: '',
    titulo: '',
    descripcion: '',
    notas: '',
    fechaSolicitada: (() => {
      const now = new Date();
      const offset = now.getTimezoneOffset() * 60000;
      const localTime = new Date(now.getTime() - offset);
      return localTime.toISOString().split('T')[0];
    })(),
    fechaProgramada: '', // Fecha para liberar al sistema
    prioridad: 'normal' as 'normal' | 'prioritaria' | 'urgente',
    tiempoEstimadoHoras: '8',
    observaciones: '',
    costo: ''
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [tiempoManualmenteModificado, setTiempoManualmenteModificado] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showClientDropdown &&
          !target.closest('.client-search-container') &&
          !target.closest('.client-option')) {
        setShowClientDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showClientDropdown]);

  const loadClients = async () => {
    try {
      const response = await clientsApi.getForAudits();

      if (response.data.success) {
        const clientsWithBranches = response.data.data.map((client: Client) => {
          let allBranches = [...(client.branches || [])];

          if (client.negociosHijos && client.negociosHijos.length > 0) {
            client.negociosHijos.forEach((negocio: any) => {
              if (negocio.branches && negocio.branches.length > 0) {
                const negocioBranches = negocio.branches.map((branch: any) => ({
                  ...branch,
                  nombre: `${negocio.nombre} - ${branch.nombre}`,
                  negocioHijoId: negocio.id,
                  negocioHijoNombre: negocio.nombre
                }));
                allBranches = [...allBranches, ...negocioBranches];
              }
            });
          }

          if (allBranches.length === 0) {
            allBranches = [
              {
                id: -1,
                nombre: 'Oficina Principal',
                direccion: client.direccion || 'Dirección no especificada',
                telefono: client.telefono
              }
            ];
          }

          return {
            ...client,
            branches: allBranches
          };
        });
        setClients(clientsWithBranches);
        setFilteredClients(clientsWithBranches);
      } else {
        alert('Error obteniendo clientes: ' + (response.data.message || 'Error desconocido'));
      }
    } catch (error) {
      alert('Error cargando clientes: ' + (error as any)?.message || 'Error de conexión');
    }
  };

  const handleClientSearch = (searchTerm: string) => {
    setClientSearchTerm(searchTerm);
    setShowClientDropdown(true);

    if (searchTerm.trim() === '') {
      setFilteredClients(clients);
    } else {
      const filtered = clients.filter(client => {
        const search = searchTerm.toLowerCase();

        // Buscar por nombre de cliente o empresa
        const matchesClient = client.nombre.toLowerCase().includes(search) ||
          (client.empresa && client.empresa.toLowerCase().includes(search));

        // Buscar por nombre de sucursal
        const matchesBranch = client.branches?.some(branch =>
          branch.nombre.toLowerCase().includes(search)
        );

        return matchesClient || matchesBranch;
      });
      setFilteredClients(filtered);
    }
  };

  const handleSelectClient = (client: Client) => {
    setSelectedClient(client);
    setClientSearchTerm(client.nombre);
    setFormData({ ...formData, clienteId: client.id.toString(), branchId: '' });
    setShowClientDropdown(false);
  };

  const handleClientChange = (clienteId: string) => {
    const cliente = clients.find(c => c.id === parseInt(clienteId));
    setSelectedClient(cliente || null);

    let tiempoEstimadoHoras = '8';
    if (cliente?.tiempo_auditoria_cliente) {
      const minutos = cliente.tiempo_auditoria_cliente;
      tiempoEstimadoHoras = (minutos / 60).toString();
    }

    setFormData(prev => ({
      ...prev,
      clienteId,
      branchId: '',
      tiempoEstimadoHoras,
      costo: cliente?.costoAuditoria?.toString() || ''
    }));

    setTiempoManualmenteModificado(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setSelectedFiles(files);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(files => files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.clienteId || !formData.titulo || !formData.branchId) {
      alert('Cliente, título y sucursal son obligatorios');
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();

      const selectedBranch = selectedClient?.branches?.find(b => b.id.toString() === formData.branchId);
      const finalClienteId = selectedBranch?.negocioHijoId || formData.clienteId;

      // IMPORTANTE: Marcar como auditoría PROGRAMADA
      submitData.append('origenCarga', 'programada');

      submitData.append('clienteId', finalClienteId);
      submitData.append('sucursal', selectedBranch?.nombre || 'Oficina Principal');
      submitData.append('direccion', selectedBranch?.direccion || selectedClient?.direccion || '');

      if (selectedBranch && selectedBranch.id !== -1) {
        submitData.append('branchId', selectedBranch.id.toString());
      }
      submitData.append('fechaSolicitada', formData.fechaSolicitada);

      // Agregar fecha programada si se especifica
      if (formData.fechaProgramada) {
        submitData.append('fechaProgramada', formData.fechaProgramada);
      }

      submitData.append('prioridad', formData.prioridad);
      if (formData.observaciones) submitData.append('observaciones', formData.observaciones);

      if (formData.titulo) submitData.append('titulo', formData.titulo);
      if (formData.descripcion) submitData.append('descripcion', formData.descripcion);
      if (formData.notas) submitData.append('notas', formData.notas);

      if (user.role === 'admin' && tiempoManualmenteModificado && formData.tiempoEstimadoHoras) {
        submitData.append('tiempoEstimadoHoras', formData.tiempoEstimadoHoras);
      }

      selectedFiles.forEach(file => {
        submitData.append('files', file);
      });

      const token = localStorage.getItem('token');
      const apiUrl = window.location.hostname === 'localhost' ?
        'http://localhost:4000/api/audits' :
        `${window.location.protocol}//${window.location.host}/api/audits`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: submitData
      });

      const result = await response.json();

      if (response.ok && result.success) {
        alert('✅ Auditoría programable creada exitosamente. Quedará retenida en Cola de Gestión hasta ser habilitada.');
        onSuccess();
      } else {
        console.error('❌ Error en respuesta:', { status: response.status, result });
        alert('❌ Error: ' + (result.message || result.error || 'No se pudo crear la auditoría'));
      }
    } catch (error: any) {
      console.error('Error creando auditoría:', error);
      alert('❌ Error: ' + (error.message || 'Error interno del servidor'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card" style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        borderBottom: '3px solid #f59e0b',
        paddingBottom: '1rem'
      }}>
        <div>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', margin: 0, color: '#f59e0b' }}>
            📅 Crear Auditoría Programable
          </h2>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0.5rem 0 0 0' }}>
            Esta auditoría quedará retenida en Cola de Gestión hasta ser habilitada manualmente
          </p>
        </div>
        <button
          onClick={onCancel}
          className="btn btn-secondary"
          style={{ fontSize: '0.875rem' }}
        >
          ✕
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {/* Cliente */}
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              👤 Cliente *
            </label>
            <div className="client-search-container" style={{ position: 'relative' }}>
              <input
                type="text"
                value={clientSearchTerm}
                onChange={(e) => handleClientSearch(e.target.value)}
                onFocus={() => setShowClientDropdown(true)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSelectedIndex(prev => prev < filteredClients.length - 1 ? prev + 1 : prev);
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    if (selectedIndex >= 0 && filteredClients[selectedIndex]) {
                      handleSelectClient(filteredClients[selectedIndex]);
                    }
                  } else if (e.key === 'Escape') {
                    setShowClientDropdown(false);
                    setSelectedIndex(-1);
                  }
                }}
                placeholder="Buscar cliente por nombre..."
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '1rem'
                }}
              />

              {showClientDropdown && filteredClients.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderTop: 'none',
                  borderRadius: '0 0 0.375rem 0.375rem',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 9999,
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  pointerEvents: 'auto'
                }}>
                  {filteredClients.map((cliente, index) => (
                    <div
                      key={cliente.id}
                      className="client-option"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectClient(cliente);
                      }}
                      onMouseEnter={() => setSelectedIndex(index)}
                      style={{
                        padding: '0.75rem',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f3f4f6',
                        transition: 'background-color 0.15s',
                        backgroundColor: selectedIndex === index ? '#e5e7eb' : 'white'
                      }}
                    >
                      <div style={{ fontWeight: '500' }}>{cliente.nombre}</div>
                      {cliente.empresa && cliente.empresa !== cliente.nombre && (
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          {cliente.empresa}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {showClientDropdown && clientSearchTerm && filteredClients.length === 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderTop: 'none',
                  borderRadius: '0 0 0.375rem 0.375rem',
                  padding: '0.75rem',
                  color: '#6b7280',
                  fontSize: '0.875rem',
                  zIndex: 10
                }}>
                  No se encontraron clientes que coincidan con "{clientSearchTerm}"
                </div>
              )}

              {showClientDropdown && !clientSearchTerm && clients.length === 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderTop: 'none',
                  borderRadius: '0 0 0.375rem 0.375rem',
                  padding: '0.75rem',
                  color: '#ef4444',
                  fontSize: '0.875rem',
                  zIndex: 10
                }}>
                  ⚠️ No hay clientes disponibles. Contacte al administrador.
                </div>
              )}
            </div>

            {selectedClient && (
              <div style={{
                marginTop: '0.5rem',
                padding: '0.75rem',
                backgroundColor: '#fef3c7',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                border: '1px solid #fbbf24'
              }}>
                <div><strong>📧 Email:</strong> {selectedClient.email || 'No disponible'}</div>
                {selectedClient.telefono && <div><strong>📞 Teléfono:</strong> {selectedClient.telefono}</div>}
                {selectedClient.empresa && <div><strong>🏢 Empresa:</strong> {selectedClient.empresa}</div>}
                {selectedClient.direccion && <div><strong>📍 Dirección:</strong> {selectedClient.direccion}</div>}
              </div>
            )}
          </div>

          {/* Sucursal */}
          {selectedClient && (
            <div>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                🏢 Sucursal *
              </label>
              <select
                value={formData.branchId}
                onChange={(e) => setFormData(prev => ({ ...prev, branchId: e.target.value }))}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '1rem'
                }}
              >
                <option value="">Seleccionar sucursal</option>
                {selectedClient.branches?.map(branch => (
                  <option key={branch.id} value={branch.id}>
                    {branch.nombre} - {branch.direccion}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Título */}
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              📋 Título de la auditoría *
            </label>
            <input
              type="text"
              value={formData.titulo}
              onChange={(e) => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
              placeholder="Ej: Auditoría fiscal 2024, Revisión de procesos..."
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '1rem'
              }}
            />
          </div>

          {/* Fechas */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                📅 Fecha Solicitada *
              </label>
              <input
                type="date"
                value={formData.fechaSolicitada}
                onChange={(e) => setFormData(prev => ({ ...prev, fechaSolicitada: e.target.value }))}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '1rem'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                🕐 Fecha Programada (opcional)
              </label>
              <input
                type="date"
                value={formData.fechaProgramada}
                onChange={(e) => setFormData(prev => ({ ...prev, fechaProgramada: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #f59e0b',
                  borderRadius: '0.375rem',
                  fontSize: '1rem',
                  backgroundColor: '#fffbeb'
                }}
              />
              <small style={{color: '#92400e', fontSize: '0.85rem', display: 'block', marginTop: '4px'}}>
                💡 Fecha en que se liberará al sistema de tránsito
              </small>
            </div>
          </div>

          {/* Prioridad */}
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              🎯 Prioridad *
            </label>
            <select
              value={formData.prioridad}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                prioridad: e.target.value as 'normal' | 'prioritaria' | 'urgente'
              }))}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '1rem'
              }}
            >
              <option value="normal">🟢 Normal</option>
              <option value="prioritaria">🟡 Prioritaria</option>
              <option value="urgente">🔴 Urgente</option>
            </select>
          </div>

          {/* Campos solo para admin */}
          {user.role === 'admin' && (
            <>
              <div>
                <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                  💰 Costo estimado
                </label>
                <input
                  type="number"
                  value={formData.costo}
                  onChange={(e) => setFormData(prev => ({ ...prev, costo: e.target.value }))}
                  placeholder="Ej: 15000"
                  min="0"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                  ⏱️ Tiempo estimado (horas)
                </label>
                <input
                  type="number"
                  value={formData.tiempoEstimadoHoras}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, tiempoEstimadoHoras: e.target.value }));
                    setTiempoManualmenteModificado(true);
                  }}
                  placeholder="Ej: 8"
                  min="0.5"
                  step="0.5"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </>
          )}

          {/* Descripción */}
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              📝 Descripción de la auditoría
            </label>
            <textarea
              value={formData.descripcion}
              onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
              placeholder="Describe los objetivos y alcance de la auditoría..."
              rows={3}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '1rem',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Notas */}
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              📋 Notas e instrucciones
            </label>
            <textarea
              value={formData.notas}
              onChange={(e) => setFormData(prev => ({ ...prev, notas: e.target.value }))}
              placeholder="Instrucciones específicas, metodología a seguir, consideraciones especiales..."
              rows={4}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '1rem',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Archivos adjuntos */}
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              📎 Archivos adjuntos
            </label>
            <div style={{
              border: '2px dashed #d1d5db',
              borderRadius: '0.375rem',
              padding: '1.5rem',
              textAlign: 'center',
              backgroundColor: '#f9fafb'
            }}>
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
                id="file-upload"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip,.rar"
              />
              <label htmlFor="file-upload" style={{
                display: 'inline-block',
                padding: '0.75rem 1.5rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '1rem'
              }}>
                📎 Seleccionar archivos
              </label>
              <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                PDF, Word, Excel, imágenes, ZIP
              </div>
            </div>

            {selectedFiles.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <p style={{ fontWeight: '500', marginBottom: '0.5rem' }}>
                  📁 Archivos seleccionados ({selectedFiles.length}):
                </p>
                {selectedFiles.map((file, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.5rem',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '0.375rem',
                    marginBottom: '0.5rem'
                  }}>
                    <span style={{ fontSize: '0.875rem' }}>
                      📄 {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      style={{
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.25rem',
                        padding: '0.25rem 0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Observaciones finales */}
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              💬 Observaciones adicionales
            </label>
            <textarea
              value={formData.observaciones}
              onChange={(e) => setFormData(prev => ({ ...prev, observaciones: e.target.value }))}
              placeholder="Cualquier observación adicional..."
              rows={3}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '1rem',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Aviso importante */}
          <div style={{
            padding: '1rem',
            backgroundColor: '#fef3c7',
            border: '2px solid #f59e0b',
            borderRadius: '0.5rem',
            fontSize: '0.875rem'
          }}>
            <strong style={{ color: '#92400e' }}>⚠️ Importante:</strong>
            <ul style={{ margin: '0.5rem 0 0 1.5rem', color: '#78350f' }}>
              <li>Esta auditoría <strong>NO irá directamente al sistema de tránsito</strong></li>
              <li>Quedará retenida en <strong>Cola de Gestión</strong> hasta ser habilitada manualmente</li>
              <li>Podrás asignarle un operador o liberarla a una fecha específica desde la sección de gestión</li>
            </ul>
          </div>

          {/* Botones */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-secondary"
              disabled={loading}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer'
              }}
            >
              ❌ Cancelar
            </button>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !formData.clienteId || !formData.titulo || !formData.branchId}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: loading ? '#9ca3af' : '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 'bold'
              }}
            >
              {loading ? '⏳ Creando...' : '📅 Crear Auditoría Programable'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateProgrammedAuditForm;
